// @ts-nocheck
import axios from "axios";
import useSWR, { cache } from "swr";
import fetcher from "middleware/api";
import config from "../constants/config";

const getFetcher = (url) => axios.get(url).then((res) => res.data);

const baseUrl = config.baseUrl;
const KEYCLOAK = "KEYCLOAK";
const AIMATCHINGENGINE = "AIMATCHINGENGINE";

const inFlightRequests = new Map();

const isApiErrorResponse = (response) => Boolean(response && typeof response === "object" && response.isApiError);

const getCacheKey = (endpoint, authenticated = true, baseUrlPath = "", data = null) => {
  const dataStr = data ? (typeof data === "object" ? JSON.stringify(data) : String(data)) : "";
  return `get:${authenticated ? "auth" : "noauth"}:${baseUrlPath || ""}:${endpoint}${dataStr ? ":" + dataStr : ""}`;
};

// Clear cached GET queries upon any successful mutation
const clearGetCache = () => {
  if (cache && typeof cache.keys === "function") {
    for (const key of cache.keys()) {
      if (typeof key === "string" && key.startsWith("get:")) {
        cache.delete(key);
      }
    }
  }
};

export const invalidateGetCache = clearGetCache;


export const useSWRGet = (path) => {
  if (!path) {
    throw new Error("Path is required");
  }

  const url = baseUrl + path;
  const { data: states, error, mutate } = useSWR(url, getFetcher);

  return { states, error, mutate };
};

export const useSWRPost = (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const authenticated = true;
  const method = "post";
  const baseUrlPath = "";
  const cacheKey = getCacheKey(endpoint, authenticated, baseUrlPath, data);
  const response = useSWR(cacheKey, () => fetcher(method, endpoint, data, authenticated, baseUrlPath));

  return response;
};

export const useSWRRequest = (endpoint, data = null, baseUrlPath = "") => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const cacheKey = getCacheKey(endpoint, true, baseUrlPath, data);
  const response = useSWR(cacheKey, () => fetcher("get", endpoint, data, true, baseUrlPath));

  return response;
};

export const useGet = async (endpoint, baseUrlPath) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "get";
  const cacheKey = getCacheKey(endpoint, true, baseUrlPath);

  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const promise = fetcher(method, endpoint, null, true, baseUrlPath)
    .then((response) => {
      if (!isApiErrorResponse(response)) {
        cache.set(cacheKey, response);
      }
      inFlightRequests.delete(cacheKey);
      return response;
    })
    .catch((error) => {
      inFlightRequests.delete(cacheKey);
      throw error;
    });

  inFlightRequests.set(cacheKey, promise);
  return promise;
};

export const usePost = async (endpoint, data, baseUrlPath) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "post";
  const { response } = await fetcher(method, endpoint, data, true, baseUrlPath);
  clearGetCache();
  return response;
};

export const usePostKeycloak = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "post";
  const { response } = await fetcher(method, endpoint, data, true, KEYCLOAK);
  clearGetCache();
  return response;
};

export const usePostAiMatching = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "post";
  const { response } = await fetcher(method, endpoint, data, true, AIMATCHINGENGINE);
  clearGetCache();
  return response;
};

export const useDelete = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "delete";
  const { response } = await fetcher(method, endpoint, data);
  clearGetCache();
  return response;
};

export const useDeleteKeycloak = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "delete";
  const { response } = await fetcher(method, endpoint, data, true, KEYCLOAK);
  clearGetCache();
  return response;
};

export const usePut = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Endpoint is required");
  }

  try {
    const method = "put";
    const { response } = await fetcher(method, endpoint, data);
    clearGetCache();
    return response;
  } catch (error) {
    return { error };
  }
};

export const usePutKeycloak = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "put";
  const { response } = await fetcher(method, endpoint, data, true, KEYCLOAK);
  clearGetCache();
  return response;
};

export const usePatch = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "patch";
  const { response } = await fetcher(method, endpoint, data);
  clearGetCache();
  return response;
};

export const useGetWithBaseUrl = async (endpoint, baseUrl) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  if (!baseUrl) {
    throw new Error("Base Url is required");
  }
  const method = "get";
  const cacheKey = getCacheKey(endpoint, true, baseUrl);

  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const promise = fetcher(method, endpoint, true, baseUrl)
    .then((response) => {
      if (!isApiErrorResponse(response)) {
        cache.set(cacheKey, response);
      }
      inFlightRequests.delete(cacheKey);
      return response;
    })
    .catch((error) => {
      inFlightRequests.delete(cacheKey);
      throw error;
    });

  inFlightRequests.set(cacheKey, promise);
  return promise;
};

//withoutBearToken
export const useGetWithOutBearerToken = async (endpoint) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "get";
  const cacheKey = getCacheKey(endpoint, false);

  if (cache.has(cacheKey)) {
    return cache.get(cacheKey);
  }

  if (inFlightRequests.has(cacheKey)) {
    return inFlightRequests.get(cacheKey);
  }

  const promise = fetcher(method, endpoint, false)
    .then((response) => {
      if (!isApiErrorResponse(response)) {
        cache.set(cacheKey, response);
      }
      inFlightRequests.delete(cacheKey);
      return response;
    })
    .catch((error) => {
      inFlightRequests.delete(cacheKey);
      throw error;
    });

  inFlightRequests.set(cacheKey, promise);
  return promise;
};

export const usePostWithOutBearerToken = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "post";
  const { response } = await fetcher(method, endpoint, data, false);
  clearGetCache();
  return response;
};

export const useDeleteWithOutBearerToken = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "delete";
  const { response } = await fetcher(method, endpoint, data, false);
  clearGetCache();
  return response;
};

export const usePutWithOutBearerToken = async (endpoint, data) => {
  if (!endpoint) {
    throw new Error("Path is required");
  }
  const method = "put";
  const { response } = await fetcher(method, endpoint, data, false);
  clearGetCache();
  return response;
};
